import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { adminSendBrevoEmail } from "@/lib/brevo-send.functions";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Send, Paperclip, X } from "lucide-react";

const MAX_RECIPIENTS = 2000;
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB

export const Route = createFileRoute("/harikaradmin/send-email")({
  head: () => ({ meta: [{ title: "Mail Gönder — JUËN" }] }),
  component: SendEmailPage,
});

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

function SendEmailPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState("J bakiyenle artık her şey cebinde — Haziran'da JUËN'de 🚀");
  const [body, setBody] = useState(`Senin için güzel haberlerimiz var. 🎉

Artık hesabına istediğin zaman online J paketi yükleyebilirsin. juen.club üzerinde "Bakiyem" sayfasına gir, paketini seç ve birkaç saniye içinde bakiyen hazır olsun. ⚡

Etkinliklerde içeceğini bu J bakiyenle satın al — kasada beklemek yok, kart çıkarmak yok. 💳⛔

Üstelik bakiyenle aldığın her içecekten %5'ini J olarak geri kazanıyorsun. Ne kadar içersen, bir sonrakine o kadar yaklaşıyorsun. 🔄✨

Bir de en sevdiğimiz kısım: aldığın içecekleri "Kuponlarım" sayfasından arkadaşlarına anında transfer edebilirsin. Paylaşmak güzeldir. 🎁🤝

Haziran'da JUËN'de:
• 20 Haziran Cuma — Can Sankır (Live)
• 27 Haziran Cuma — Analogdisko

Görüşmek üzere. 🖤`);
  const [sending, setSending] = useState(false);
  const [sendAll, setSendAll] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  const handleFilesPicked = (files: FileList | null) => {
    if (!files) return;
    const incoming = Array.from(files);
    const merged = [...attachments];
    for (const f of incoming) {
      if (merged.length >= MAX_ATTACHMENTS) {
        toast.error(`En fazla ${MAX_ATTACHMENTS} dosya eklenebilir`);
        break;
      }
      if (f.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`${f.name} 10MB'dan büyük`);
        continue;
      }
      merged.push(f);
    }
    setAttachments(merged);
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const PAGE = 1000;
      const all: Member[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, email")
          .eq("membership_status", "approved")
          .order("first_name")
          .range(from, from + PAGE - 1);
        if (error) {
          toast.error(error.message);
          break;
        }
        const batch = (data as Member[]) ?? [];
        all.push(...batch);
        if (batch.length < PAGE) break;
      }
      setMembers(all);
    })();
  }, [isAdmin]);


  const filtered = useMemo(() => {
    if (!query.trim()) return members;
    const q = query.toLowerCase();
    return members.filter(
      (m) =>
        m.first_name.toLowerCase().includes(q) ||
        m.last_name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q),
    );
  }, [members, query]);

  const toggle = (id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAllVisible = () => {
    const visibleIds = filtered.map((m) => m.id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const handleSend = async () => {
    if (!subject.trim()) return toast.error("Konu yaz");
    if (!body.trim()) return toast.error("Mesaj yaz");
    const targets = sendAll ? members : members.filter((m) => selected.has(m.id));
    if (targets.length === 0) return toast.error("Alıcı yok");
    if (targets.length > MAX_RECIPIENTS) {
      return toast.error(`Tek seferde en fazla ${MAX_RECIPIENTS} kişiye gönderilebilir`);
    }
    if (!confirm(`${targets.length} kişiye mail gönderilecek. Devam edilsin mi?`)) return;

    setSending(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      setSending(false);
      return toast.error("Oturum bulunamadı, tekrar giriş yap");
    }

    try {
      // Upload attachments to storage and collect public URLs
      const uploadedAttachments: { url: string; name: string }[] = [];
      for (const file of attachments) {
        const ext = file.name.split(".").pop() || "bin";
        const path = `${user!.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("email-attachments")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) {
          setSending(false);
          return toast.error(`Dosya yüklenemedi: ${file.name} (${upErr.message})`);
        }
        const { data: pub } = supabase.storage.from("email-attachments").getPublicUrl(path);
        uploadedAttachments.push({ url: pub.publicUrl, name: file.name });
      }

      const result = await adminSendBrevoEmail({
        data: {
          accessToken: session.access_token,
          subject: subject.trim(),
          body: body.trim(),
          recipients: targets.map((m) => ({ email: m.email, firstName: m.first_name })),
          attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
        },
      });
      setSending(false);
      if (!result.success) {
        return toast.error(result.error || "Gönderim başarısız");
      }
      const ok = result.results.filter((r) => r.ok).length;
      const fail = result.results.length - ok;
      if (fail === 0) {
        toast.success(`${ok} mail gönderildi`);
        setSelected(new Set());
        setSubject("");
        setBody("");
        setAttachments([]);
      } else {
        toast.error(`${ok} başarılı, ${fail} başarısız`);
      }
    } catch (e) {
      setSending(false);
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Gönderim hatası");
    }
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Mail Gönder</h1>
          <p className="mt-2 text-muted-foreground">
            Onaylı üyelerden seçim yap, konu ve mesaj yaz. Tek seferde en fazla {MAX_RECIPIENTS} kişi.
          </p>
        </div>

        <AdminTabs />

        <div className="grid gap-6 md:grid-cols-[320px_1fr]">
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Üye ara..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{selected.size} seçili</span>
              <button
                type="button"
                onClick={toggleAllVisible}
                className="text-muted-foreground hover:text-foreground"
              >
                Görünenleri seç/kaldır
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-border bg-card shadow-card">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">Üye yok</div>
              ) : (
                filtered.map((m) => (
                  <label
                    key={m.id}
                    className="flex cursor-pointer items-start gap-3 border-b border-border px-4 py-3 text-sm transition-colors last:border-0 hover:bg-muted"
                  >
                    <Checkbox
                      checked={selected.has(m.id)}
                      onCheckedChange={() => toggle(m.id)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{m.first_name} {m.last_name}</div>
                      <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Konu</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Örn: Bu hafta sonu için bir hatırlatma"
                maxLength={120}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Mesaj</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Üyelere ileteceğin mesajı yaz. Boş satır bırakarak paragraf ayırabilirsin."
                rows={12}
                maxLength={4000}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Mail "Merhaba {`{ad}`}," ile başlar ve JUËN imzasıyla biter. HTML kabul edilmez.
              </p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Ekler (opsiyonel)</label>
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted">
                <Paperclip className="h-4 w-4" />
                <span>PDF veya fotoğraf ekle (maks {MAX_ATTACHMENTS} dosya, 10MB)</span>
                <input
                  type="file"
                  multiple
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    handleFilesPicked(e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
              {attachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {attachments.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between rounded-md bg-muted px-3 py-1.5 text-xs"
                    >
                      <span className="truncate">
                        {f.name}{" "}
                        <span className="text-muted-foreground">
                          ({(f.size / 1024).toFixed(0)} KB)
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        className="ml-2 text-muted-foreground hover:text-foreground"
                        aria-label="Kaldır"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox checked={sendAll} onCheckedChange={(v) => setSendAll(v === true)} />
                <span>Tüm onaylı üyelere gönder ({members.length})</span>
              </label>
              <Button
                onClick={handleSend}
                disabled={sending || (!sendAll && selected.size === 0) || !subject.trim() || !body.trim()}
              >
                <Send className="mr-1.5 h-4 w-4" />
                {sending ? "Gönderiliyor..." : `${sendAll ? members.length : selected.size} kişiye gönder`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
